import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Pencil, Trash2, Package, Loader2, Star } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";

const EMPTY = {
  name: "", category: "", sku: "", barcode: "", hsn: "", purchase_price: 0, selling_price: 0,
  mrp: 0, tax_rate: 0, tax_type: "none", opening_stock: 0, current_stock: 0, unit: "PCS",
  min_stock: 0, location: "", expiry_date: "",
};

export default function Products() {
  const { isSuperAdmin, hasPerm } = useAuth();
  const canEdit = hasPerm("edit_product");
  const canDelete = hasPerm("delete_product");
  const canRate = hasPerm("change_product_rate");
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const { data: cats } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["products", search, category, stockFilter, sort, page],
    queryFn: async () => (await api.get("/products", { params: { search, category, stock_filter: stockFilter, sort, page, limit: 12 } })).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      const body = { ...payload };
      ["purchase_price", "selling_price", "mrp", "tax_rate", "opening_stock", "current_stock", "min_stock"].forEach((k) => (body[k] = Number(body[k]) || 0));
      body.tax_type = body.tax_rate > 0 ? "gst" : "none";
      if (!body.expiry_date) body.expiry_date = null;
      if (editing) return (await api.put(`/products/${editing}`, body)).data;
      return (await api.post("/products", body)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Product updated" : "Product created");
      qc.invalidateQueries({ queryKey: ["products"] });
      setDialogOpen(false);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/products/${id}`)).data,
    onSuccess: () => { toast.success("Product deleted"); qc.invalidateQueries({ queryKey: ["products"] }); setDeleteId(null); },
    onError: (e) => toast.error(apiError(e)),
  });

  const favMut = useMutation({
    mutationFn: async ({ id, favorite }) => (await api.patch(`/products/${id}/favorite`, { favorite })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
    onError: (e) => toast.error(apiError(e)),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setDialogOpen(true); }
  function openEdit(p) { setEditing(p.id); setForm({ ...EMPTY, ...p, expiry_date: p.expiry_date || "" }); setDialogOpen(true); }

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 12) || 1;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0F172A]">Products / Inventory</h2>
          <p className="text-slate-500 text-sm">{num(total)} items · Manage your catalog & stock</p>
        </div>
        {hasPerm("create_product") && (
          <Button onClick={openCreate} className="bg-[#F4571E] hover:bg-[#D9420D] gap-2" data-testid="create-product-btn">
            <Plus className="h-4 w-4" /> Create Item
          </Button>
        )}
      </div>

      <Card className="p-4 border-slate-200">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="product-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search name, SKU, barcode..." className="pl-9" />
          </div>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger data-testid="product-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-white/95 backdrop-blur-md">
              <SelectItem value="all">All Categories</SelectItem>
              {(cats || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stockFilter} onValueChange={(v) => { setStockFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="product-stock-filter"><SelectValue placeholder="Stock" /></SelectTrigger>
            <SelectContent className="bg-white/95 backdrop-blur-md">
              <SelectItem value="all">All Stock</SelectItem>
              <SelectItem value="in">In Stock</SelectItem>
              <SelectItem value="low">Low Stock</SelectItem>
              <SelectItem value="out">Out of Stock</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="cursor-pointer" onClick={() => setSort(sort === "name" ? "-name" : "name")}>Item</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Purchase</TableHead>
                <TableHead className="text-right">Selling</TableHead>
                <TableHead className="text-right">GST</TableHead>
                <TableHead className="text-right cursor-pointer" onClick={() => setSort(sort === "current_stock" ? "-current_stock" : "current_stock")}>Stock</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>
              ) : items.length ? items.map((p) => {
                const low = p.current_stock <= p.min_stock;
                return (
                  <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-800">{p.name}</div>
                      <div className="text-xs text-slate-400 font-mono">{p.sku} {p.barcode && `· ${p.barcode}`}</div>
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="font-normal">{p.category || "—"}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-slate-600">{money(p.purchase_price)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-[#1A2A5E]">{money(p.selling_price)}</TableCell>
                    <TableCell className="text-right font-mono text-slate-500">{p.tax_rate}%</TableCell>
                    <TableCell className="text-right">
                      <Badge className={low ? "bg-rose-50 text-rose-700 hover:bg-rose-50" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-50"}>
                        {num(p.current_stock)} {p.unit}
                      </Badge>
                    </TableCell>
                    {isSuperAdmin ? null : null}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => favMut.mutate({ id: p.id, favorite: !p.favorite })} data-testid={`fav-product-${p.id}`}>
                          <Star className={`h-4 w-4 ${p.favorite ? "fill-[#F4571E] text-[#F4571E]" : "text-slate-400"}`} />
                        </Button>
                        {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                        {canDelete && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDeleteId(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />No products found
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-slate-100">
            <span className="text-xs text-slate-400">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="products-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="products-next">Next</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">{editing ? "Edit Product" : "Create Item"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Item Name *</Label>
              <Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="product-form-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white/95 backdrop-blur-md">
                  {(cats || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1" /></div>
            <div><Label>SKU / Product Code</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1" /></div>
            <div><Label>Barcode</Label><Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="mt-1" /></div>
            <div><Label>HSN / SAC</Label><Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className="mt-1" /></div>
            <div><Label>GST %</Label><Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="mt-1" /></div>
            <div><Label>Purchase Price</Label><Input type="number" disabled={!isSuperAdmin && !canRate} value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="mt-1" /></div>
            <div><Label>Selling Price {!isSuperAdmin && !canRate && <span className="text-[10px] text-amber-600">(locked)</span>}</Label><Input type="number" disabled={!isSuperAdmin && !canRate} value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} className="mt-1" data-testid="product-selling-price" /></div>
            <div><Label>MRP</Label><Input type="number" disabled={!isSuperAdmin && !canRate} value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="mt-1" /></div>
            <div><Label>{editing ? "Current Stock" : "Opening Stock"}</Label><Input type="number" value={editing ? form.current_stock : form.opening_stock} onChange={(e) => setForm({ ...form, [editing ? "current_stock" : "opening_stock"]: e.target.value, current_stock: e.target.value })} className="mt-1" /></div>
            <div><Label>Minimum Stock</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="mt-1" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
            <div><Label>Expiry Date</Label><Input type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1" /></div>
            {hasPerm("product_photo") && <div className="sm:col-span-2"><Label>Product Photo</Label><div className="mt-1"><ImageUpload value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="Photo" /></div></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#1A2A5E] hover:bg-[#223678]" disabled={!form.name || saveMut.isPending} onClick={() => saveMut.mutate(form)} data-testid="product-form-save">
              {saveMut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. The product will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => delMut.mutate(deleteId)} data-testid="confirm-delete-product">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
